/**
 * ProfilePicker — de tegels op een gedeeld apparaat.
 *
 * Toont ALLEEN naam, rol en avatar. Geen voortgang, geen streak, geen sterren.
 * Dat is geen bezuiniging maar een grens: zodra de kiezer prestaties toont,
 * wordt hij een vergelijkingsscherm tussen broers en zussen — didactisch
 * schadelijk, en een gegevensverstrekking waar niemand toestemming voor gaf.
 *
 * Een tegel logt NIET in. Hij vult het inlogveld voor; het wachtwoord blijft
 * vereist. Zie `deviceRoster.ts` voor waarom er geen sessies worden bewaard.
 *
 * @see docs/specs/PROFILE_SWITCHER_DESIGN.md
 */

import React from 'react';
import { motion } from 'framer-motion';
import { UserRound, GraduationCap, Heart, Shield, X, Plus } from 'lucide-react';
import type { RosterProfile } from '../../data/supabase/deviceRoster';
const ROLE_ICON = {
  learner: UserRound,
  parent: Heart,
  teacher: GraduationCap,
  admin: Shield
} as const;
const ROLE_LABEL = {
  learner: 'Leerling',
  parent: 'Ouder',
  teacher: 'Leerkracht',
  admin: 'Beheerder'
} as const;
interface ProfilePickerProps {
  profiles: RosterProfile[];
  onSelect: (profile: RosterProfile) => void;
  onSomeoneElse: () => void;
  onForget: (id: string) => void;
}
export function ProfilePicker({
  profiles,
  onSelect,
  onSomeoneElse,
  onForget
}: ProfilePickerProps) {
  return <section aria-labelledby="picker-heading" data-id="element-185" data-mp="2liwd6e7rbybe">
      <h2 id="picker-heading" className="text-xl font-black text-slate-900 mb-1" data-id="element-186" data-mp="2lk7ca0q9chw6">
        Wie ben jij?
      </h2>
      <p className="text-sm text-slate-500 mb-5" data-id="element-187" data-mp="3e7boapnrpwhg">
        Kies je naam en vul daarna je wachtwoord in.
      </p>

      <ul className="grid grid-cols-2 gap-3" data-id="element-188" data-mp="39fju855el8mk">
        {profiles.map((profile, i) => {
        const Icon = ROLE_ICON[profile.role] ?? UserRound;
        return <motion.li key={profile.id} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.04,
          duration: 0.25
        }} className="relative" data-id="element-189" data-mp="5r8ead96nvpy" data-mpi={i}>
              <button type="button" onClick={() => onSelect(profile)} className="w-full h-full p-4 rounded-2xl bg-white border-2 border-slate-200 hover:border-orange-400 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all text-left" data-id="element-190" data-mp="1v9ow45zd88j8">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mb-3" data-id="element-191" data-mp="1qfau3kq7735l">
                  {profile.avatar ? <img src={profile.avatar} alt="" className="w-12 h-12 rounded-full object-cover" data-id="element-192" data-mp="1gkivqeoskc9r" /> : <Icon className="w-6 h-6 text-orange-600" data-id="element-193" data-mp="17jkqxu6j40q4" />}
                </div>
                <div className="font-black text-slate-900 truncate" data-id="element-194" data-mp="2wy92jt7apen3">
                  {profile.displayName}
                </div>
                <div className="text-xs font-semibold text-slate-400" data-id="element-195" data-mp="31ugbluuzn699">
                  {ROLE_LABEL[profile.role] ?? ''}
                </div>
              </button>

              {/* Een tegel moet weg kunnen zonder in de instellingen te duiken —
                  op een geleend apparaat wil je dat meteen kunnen. */}
              <button type="button" onClick={() => onForget(profile.id)} aria-label={`${profile.displayName} van dit apparaat halen`} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-white border border-slate-200 text-slate-400 hover:text-rose-600 hover:border-rose-300 flex items-center justify-center shadow-sm transition-colors" data-id="element-196" data-mp="v8tpk2dz9bs8">
                <X className="w-3.5 h-3.5" data-id="element-197" data-mp="1e033cnus6mpm" />
              </button>
            </motion.li>;
      })}

        {/* Altijd aanwezig: wie zijn tegel niet herkent, moet gewoon kunnen
            inloggen. */}
        <li data-id="element-198" data-mp="3frpt6m2xsbki">
          <button type="button" onClick={onSomeoneElse} className="w-full h-full min-h-[7.5rem] p-4 rounded-2xl border-2 border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-all text-left" data-id="element-199" data-mp="3fs91rc6p5nlv">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3" data-id="element-200" data-mp="2gpt5mejk124a">
              <Plus className="w-6 h-6 text-slate-500" data-id="element-201" data-mp="1o2ympm2dyasx" />
            </div>
            <div className="font-black text-slate-700" data-id="element-202" data-mp="1o4t4wsm9votb">Iemand anders</div>
            <div className="text-xs font-semibold text-slate-400" data-id="element-203" data-mp="1t2upxj39dy7f">Inloggen</div>
          </button>
        </li>
      </ul>
    </section>;
}